import { LoginRequest, LoginResponse, ApiError } from "../types/auth";
import {
  CreateEchoRequest,
  CreateEchoResponse,
  GetEchosParams,
  GetEchosResponse,
  SortField,
  SortOrder,
  GetEchoSessionsParams,
  GetEchoSessionsResponse,
} from "../types/echo";
import { StorageService } from "./storage-service";
import type { EchoSessionResponse } from "../types/echo-session";
import type { GetEchoReportResponse } from "../types/echo-report";
import { 
  GetProductsParams, 
  GetProductsResponse, 
  ProductSortField, 
  CreateProductRequest, 
  CreateProductResponse,
  UpdateProductRequest,
  UpdateProductResponse,
  DeleteProductResponse,
  ApiErrorResponse
} from "../types/product";
import { toast } from "react-toastify";

interface CustomError extends Error {
  response?: ApiError;
}

const BASE_URL = "https://echo.botcruit-ai.in/api/api";

const APIRoutes = {
  login: "/users/login",
  createEcho: "/echo",
  getEchoes: "/echo",
  getEchoReport: (echoSessionId: string) => `/echo/get-report/${echoSessionId}`,
  getProducts: "/product",
  createProduct: "/product",
  updateProduct: (id: string) => `/product/${id}`,
  deleteProduct: (id: string) => `/product/${id}`,
};

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

interface RequestOptions {
  method: HttpMethod;
  headers: Record<string, string>;
  body?: string;
}

interface DashboardMetricsParams {
  startDate: string;
  endDate: string;
  productId?: string;
}

interface DashboardMetricsResponse {
  success: boolean;
  message: string;
  data: {
    totalSessions: number;
    completedSessions: number;
    avgSatisfactionScore: number;
    avgRecommendationScore: number;
    feedbackTrend: {
      date: string;
      count: number;
    }[];
    emotionalDistribution: {
      sentiment: string;
      count: number;
    }[];
    sentimetTrend: {
      sentiment: string;
      date: string;
      count: number;
    }[];
    satisfactionTrend: {
      date: string;
      avg: string;
    }[];
    recommendationTrend: {
      date: string;
      avg: string;
    }[];
  };
}

class ApiService {
  private static displayError(error: any) {
    const errorMessage =
      error.response?.message ||
      error.message ||
      "An unexpected error occurred";    
    toast.error(errorMessage, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        className: "!rounded-lg !font-medium",
        style: {
          padding: "16px",
          marginBottom: "8px"
        },
    })
  }

  private static handleResponse = async (response: Response) => {
    if (!response.ok) {
      if (response.status === 401) {
        StorageService.clearStorage();
        window.location.href = "/login";
        throw new Error("Session expired. Please login again.");
      }

      const error: ApiError = await response.json();
      const customError = new Error(
        error.message || "An error occurred"
      ) as CustomError;
      customError.response = error;
    //   this.displayError(customError);
      throw customError;
    }
    return response.json();
  };

  private static async handleApiError<T>(promise: Promise<T>): Promise<T> {
    try {
      return await promise;
    } catch (error: any) {
      this.displayError(error);
      throw error;
    }
  }

  private static createHeaders(
    isAuthenticated: boolean = false
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (isAuthenticated) {
      const token = StorageService.getToken();
      if (token) {
        headers["Authorization"] = `${token}`;
      }
    }

    return headers;
  }

  static async apiCall<T>(
    endpoint: string,
    method: HttpMethod = "GET",
    body?: any,
    params?: Record<string, string>
  ): Promise<T> {
    let url = `${BASE_URL}${endpoint}`;

    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const options: RequestOptions = {
      method,
      headers: this.createHeaders(false),
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    return this.handleApiError(fetch(url, options).then(this.handleResponse));
  }

  static async apiAuthenticatedCall<T>(
    endpoint: string,
    method: HttpMethod = "GET",
    body?: any,
    params?: Record<string, string>
  ): Promise<T> {
    let url = `${BASE_URL}${endpoint}`;

    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const options: RequestOptions = {
      method,
      headers: this.createHeaders(true),
    };

    if (body) {
      options.body = JSON.stringify(body);
    }

    return this.handleApiError(fetch(url, options).then(this.handleResponse));
  }

  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await this.apiCall<LoginResponse>(
      APIRoutes.login,
      "POST",
      credentials
    );

    if (response.token) {
      StorageService.setToken(response.token);
      StorageService.setUser(JSON.stringify(response.user));
    }

    return response;
  }

  static async createEcho(
    request: CreateEchoRequest
  ): Promise<CreateEchoResponse> {
    return this.apiAuthenticatedCall<CreateEchoResponse>(
      APIRoutes.createEcho,
      "POST",
      request
    );
  }

  static async getEchos(
    params: GetEchosParams = {}
  ): Promise<GetEchosResponse> {
    const defaultParams: GetEchosParams = {
      page: 1,
      limit: 10,
      sortBy: SortField.CREATED_AT,
      sortOrder: SortOrder.DESC,
    };

    const queryParams = { ...defaultParams, ...params };

    return this.apiAuthenticatedCall<GetEchosResponse>(
      APIRoutes.getEchoes,
      "GET",
      undefined,
      queryParams as Record<string, string>
    );
  }

  static async startEchoSession(
    echoId: string,
    audienceId: string
  ): Promise<EchoSessionResponse> {
    const url = `${BASE_URL}/echo/start-session/${echoId}/${audienceId}`;
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    };
    return this.handleApiError(fetch(url, options).then(this.handleResponse));
  }

  static async submitFeedback(
    echoId: string,
    audienceId: string,
    feedback: string,
    isFirstFeedback: boolean,
    previousQuestion: string,
    previousMasterQuestion: string,
    timeTakenToAnswer: number,
    recordingKey: string,
    recordingWriteUrl: string
  ): Promise<EchoSessionResponse> {
    const url = `${BASE_URL}/echo/submit-feedback/${echoId}/${audienceId}`;
    const options = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        feedback,
        isFirstFeedback,
        previousQuestion,
        previousMasterQuestion,
        timeTakenToAnswer,
        recordingKey,
        recordingWriteUrl
      }),
    };
    return this.handleApiError(fetch(url, options).then(this.handleResponse));
  }

  static async getEchoSessions(
    echoId: string,
    params: GetEchoSessionsParams = {}
  ): Promise<GetEchoSessionsResponse> {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const url = `${BASE_URL}/echo/${echoId}/sessions?${queryParams.toString()}`;
    const options = {
      method: "GET",
      headers: this.createHeaders(true),
    };
    return this.handleApiError(fetch(url, options).then(this.handleResponse));
  }

  static async getEchoReport(
    echoSessionId: string
  ): Promise<GetEchoReportResponse> {
    return this.apiAuthenticatedCall<GetEchoReportResponse>(
      APIRoutes.getEchoReport(echoSessionId),
      "GET"
    );
  }

  static async getProducts(
    params: GetProductsParams = {}
  ): Promise<GetProductsResponse> {
    const defaultParams: GetProductsParams = {
      page: 1,
      limit: 10,
      sortBy: ProductSortField.CREATED_AT,
      sortOrder: SortOrder.DESC,
    };

    const queryParams = { ...defaultParams, ...params };

    return this.apiAuthenticatedCall<GetProductsResponse>(
      APIRoutes.getProducts,
      "GET",
      undefined,
      queryParams as Record<string, string>
    );
  }

  static async createProduct(
    request: CreateProductRequest
  ): Promise<CreateProductResponse> {
    try {
      return await this.apiAuthenticatedCall<CreateProductResponse>(
        APIRoutes.createProduct,
        "POST",
        request
      );
    } catch (error: any) {
      // Handle validation errors
      if (error.response?.success === false) {
        throw new Error(error.response.message);
      }
      throw error;
    }
  }

  static async updateProduct(
    id: string,
    request: UpdateProductRequest
  ): Promise<UpdateProductResponse> {
    try {
      return await this.apiAuthenticatedCall<UpdateProductResponse>(
        APIRoutes.updateProduct(id),
        "PUT",
        request
      );
    } catch (error: any) {
      // Handle validation errors
      if (error.response?.success === false) {
        throw new Error(error.response.message);
      }
      throw error;
    }
  }

  static async deleteProduct(
    id: string
  ): Promise<DeleteProductResponse> {
    try {
      return await this.apiAuthenticatedCall<DeleteProductResponse>(
        APIRoutes.deleteProduct(id),
        "DELETE"
      );
    } catch (error: any) {
      // Handle validation errors
      if (error.response?.success === false) {
        throw new Error(error.response.message);
      }
      throw error;
    }
  }

  static async getDashboardMetrics(params: DashboardMetricsParams): Promise<DashboardMetricsResponse> {
    return this.apiAuthenticatedCall<DashboardMetricsResponse>(
      '/echo/dashboard-metrics',
      'POST',
      params
    );
  }
}

export default ApiService;
